import React, { useEffect, useRef, useState, useCallback } from 'react';
import { KText } from '@/ui-library/components/text';
import {
  Flex,
  Box,
  Portal,
  useOutsideClick,
  useDisclosure,
} from '@chakra-ui/react';
import ReactPaginate from 'react-paginate';
import { useDispatch } from 'react-redux';
import './styles/genericPagination.css';

const formatPageCount = (count) => {
  if (count >= 1000000) {
    return `${Math.floor(count / 1000000)}M+`;
  }
  if (count >= 1000) {
    return `${Math.floor(count / 1000)}K+`;
  }
  return count.toString();
};

const LIMIT_OPTIONS = [20, 50, 100];

const PaginationComponent = ({
  pagination = {},
  number,
  applyPage,
  applyLimit,
  className = undefined,
  type = 'meta',
  isDispatch = true,
  limit = 20,
  totalKey = 'total',
  enableNthSelection = true,
  enableLimitSelection = false,
  ...rest
}) => {
  const dispatch = useDispatch();
  const { isOpen, onClose, onToggle } = useDisclosure();
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({});

  // Close dropdown when clicking outside (but not when clicking the "1-20 of 20" trigger, so it can toggle)
  useOutsideClick({
    ref: dropdownRef,
    handler: (event) => {
      const isClickOnTrigger =
        triggerRef.current && triggerRef?.current?.contains(event?.target);
      if (!isClickOnTrigger) {
        onClose();
      }
    },
    enabled: isOpen,
  });

  // Simple handler without useCallback for faster response
  const handleLimitChange = (newLimit) => {
    onClose();
    if (applyLimit && newLimit !== limit) {
      dispatch(applyLimit({ limit: newLimit }));
    }
  };

  const { page, pages } = pagination;
  const total = pagination?.[totalKey];

  let forcePage = 0;
  let pageCount = 0;
  let shouldShow = false;

  if (type === 'meta') {
    if (page && pages) {
      pageCount = pages;
      if (pageCount >= 1) {
        forcePage = page - 1;
        shouldShow = true;
      }
    }
  } else if (type === 'scroll') {
    if (total) {
      pageCount = Math.ceil(total / limit);
      if (pageCount >= 1) {
        forcePage = number || 0;
        shouldShow = true;
      }
    }
  }

  // Derived values for pagination logic
  const startItem = forcePage * limit + 1;
  const endItem = Math.min((forcePage + 1) * limit, total);

  const rangeLabel = [startItem, endItem, total].every(Number.isFinite)
    ? `${startItem}-${endItem} of ${total}`
    : null;

  // Check if last page is already visible in ReactPaginate's range or margin
  const pageRangeDisplayed = 3;
  const marginPagesDisplayed = 1;
  // Last page is visible if it's within the page range OR if margin pages are enabled
  const isLastPageInRange =
    forcePage + Math.floor(pageRangeDisplayed / 2) >= pageCount - 1 ||
    marginPagesDisplayed > 0;

  // Last page selection logic
  const LARGE_DATASET_THRESHOLD = 10000;

  // Check: Is this a small dataset? (less than 10,000 records)
  const isSmallDataset = total < LARGE_DATASET_THRESHOLD;

  // Check: Is the user near the last page? (within last 6 pages)
  // Example: If there are 100 pages, and user is on page 95 or higher → true
  const isNearLastPage = forcePage >= pageCount - 6;

  // Final decision: Can the user select the last page?
  // enableNthSelection must be true, AND one of these:
  //   1. Dataset is small (less than 10,000 records)
  //   2. User is near the end (within last 6 pages)
  const canSelectLastPage =
    enableNthSelection || isSmallDataset || isNearLastPage;

  // ============================================================
  // LAST PAGE LABEL FORMATTING
  // ============================================================
  //
  // When user is near the last page (n-6), show actual page number
  // Otherwise, show formatted version (2K+, 1M+, etc.)
  //
  // Example:
  // - On page 1 of 2178 → Show "2K+"
  // - On page 2173 of 2178 → Show "2178" (actual number)
  //
  // ============================================================
  const formattedLastPage = isNearLastPage
    ? pageCount.toString()
    : formatPageCount(pageCount);

  // Memoize handlePageClick to prevent unnecessary re-renders
  const handlePageClick = useCallback(
    ({ selected }) => {
      // Block last page selection if not allowed
      if (selected === pageCount - 1 && !canSelectLastPage) {
        return;
      }

      if (applyPage) {
        if (isDispatch) {
          dispatch(applyPage({ page: selected }));
        } else {
          applyPage(selected);
        }
      }
    },
    [applyPage, isDispatch, dispatch, pageCount, canSelectLastPage]
  );

  // Memoize pageLabelBuilder to prevent unnecessary recalculations
  const pageLabelBuilder = useCallback(
    (pageNum) => {
      // For the last page:
      // - If user is near the end (n-6), show actual number (e.g., 2178)
      // - Otherwise, show formatted version (e.g., 2K+)
      if (pageNum === pageCount) {
        return isNearLastPage ? pageNum : formatPageCount(pageNum);
      }
      return pageNum;
    },
    [pageCount, isNearLastPage]
  );

  // Calculate dropdown position in effect to avoid reading ref during render
  useEffect(() => {
    if (!isOpen) return;
    const el = triggerRef.current;
    if (!el) return;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      setDropdownPosition({
        position: 'fixed',
        bottom: `${window.innerHeight - rect.top + 8}px`,
        left: `${rect.left}px`,
        zIndex: 9999,
      });
    };
    requestAnimationFrame(measure);
  }, [isOpen]);

  useEffect(() => {
    const paginationSpacer = document.getElementById('table-pagination-spacer');
    if (paginationSpacer) {
      paginationSpacer.style.display = shouldShow ? 'block' : 'none';
    }
  }, [shouldShow]);

  if (!shouldShow) {
    return null;
  }

  return (
    <Flex
      h="34px"
      p="0 6px 0 12px"
      gap="6px"
      alignItems="center"
      justifyContent="flex-end"
      className={`genericPagination ${className && className}`}
      background="ats-bg-secondary"
      boxShadow="23px 93px 38px rgba(0, 0, 0, 0.01), 13px 52px 32px rgba(0, 0, 0, 0.03), 6px 23px 24px rgba(0, 0, 0, 0.05)"
      borderRadius="6px"
      {...rest}
    >
      {/* Range Label with Limit Dropdown */}
      {rangeLabel && (
        <Box position="relative">
          <Flex
            ref={triggerRef}
            alignItems="center"
            gap="6px"
            cursor={enableLimitSelection && applyLimit ? 'pointer' : 'default'}
            onClick={enableLimitSelection && applyLimit ? onToggle : undefined}
            mr="4px"
          >
            <KText size="sm-medium" color="base-white" whiteSpace="nowrap">
              {rangeLabel}
            </KText>
            {enableLimitSelection && applyLimit && (
              <Box
                as="span"
                transform={isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}
                transition="transform 0.2s"
                color="base-white"
                fontSize="8px"
                ml="-2px"
              >
                ▼
              </Box>
            )}
          </Flex>

          {/* Dropdown Menu - Opens Above */}
          {isOpen && enableLimitSelection && applyLimit && (
            <Portal>
              <Box
                ref={dropdownRef}
                style={dropdownPosition}
                bg="white"
                borderRadius="12px"
                boxShadow="0 4px 20px rgba(0, 0, 0, 0.15)"
                border="1px solid"
                borderColor="secondary-100"
                overflow="hidden"
                minW="120px"
                py="0"
              >
                {LIMIT_OPTIONS.map((option) => (
                  <Box
                    key={option}
                    px="12px"
                    py="6px"
                    cursor="pointer"
                    bg={option === limit ? 'primary-50' : 'white'}
                    _hover={{ bg: 'secondary-50' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleLimitChange(option);
                    }}
                  >
                    <KText
                      size="sm"
                      color={option === limit ? 'primary-600' : 'secondary-700'}
                    >
                      {option} per page
                    </KText>
                  </Box>
                ))}
              </Box>
            </Portal>
          )}
        </Box>
      )}
      <Flex
        alignItems="center"
        className={`paginationWrapper ${
          !canSelectLastPage ? 'lastPageDisabled' : ''
        }`}
      >
        <ReactPaginate
          breakLabel="..."
          breakClassName="paginationBreak"
          breakLinkClassName="paginationBreakLink"
          previousLabel="‹"
          nextLabel="›"
          previousLinkClassName="previousPaginationClass"
          nextLinkClassName="nextPaginationClass"
          forcePage={forcePage}
          onPageChange={handlePageClick}
          onClick={({ isBreak }) => (isBreak ? false : undefined)}
          pageCount={pageCount}
          pageRangeDisplayed={5}
          marginPagesDisplayed={1}
          pageLabelBuilder={pageLabelBuilder}
        />
        {!isLastPageInRange && (
          <span
            className={`lastPageLabel ${
              canSelectLastPage ? 'lastPageSelectable' : ''
            }`}
            onClick={() => {
              if (canSelectLastPage) {
                handlePageClick({ selected: pageCount - 1 });
              }
            }}
          >
            {formattedLastPage}
          </span>
        )}
        <button
          className="customNextButton"
          onClick={() => {
            if (forcePage < pageCount - 1) {
              handlePageClick({ selected: forcePage + 1 });
            }
          }}
          disabled={forcePage >= pageCount - 1}
        >
          ›
        </button>
      </Flex>
    </Flex>
  );
};

// Wrap Pagination in React.memo to prevent unnecessary re-renders
export const Pagination = React.memo(PaginationComponent);
